import { CandidateForm } from './CandidateForm';
import { JobForm } from './JobForm';
import { AnalysisResults } from '../analysis/AnalysisResults';
import { AnalysisLoading } from '../analysis/AnalysisLoading';
import { useUploadForm } from './hooks/useUploadForm';
import { useSubscription, canPerformAnalysis } from '../../hooks/useSubscription';
import { UsageIndicator } from '../pricing/UsageIndicator';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { UploadCloud, RotateCcw, PlusCircle, Zap, Loader2 } from 'lucide-react';
import { useEffect } from 'react';

interface ProjectRequirements {
  experience: string;
  skills: string[];
  education: string;
}

interface UploadFormProps {
  projectId?: string;
  projectRequirements?: ProjectRequirements;
  onAnalysisComplete?: (analysisResult: any) => void;
}

export function UploadForm({ projectId, projectRequirements, onAnalysisComplete }: UploadFormProps) {
  const { values, errors, setFieldValue, handleSubmit, analysis, isLoading, resetFormAndAnalysis } = useUploadForm();
  const { subscription, isLoading: subLoading } = useSubscription();

  // Auto-populate job description when used within a project
  useEffect(() => {
    if (projectRequirements && !values.jobDescription) {
      const jobDescription = `
Experience Required: ${projectRequirements.experience}
Education: ${projectRequirements.education}

Required Skills:
${projectRequirements.skills.map(skill => `• ${skill}`).join('\n')}

Please analyze this candidate's resume against these specific requirements for this project.
      `.trim();
      
      setFieldValue('jobDescription', jobDescription);
    }
  }, [projectRequirements, values.jobDescription, setFieldValue]);

  const userCanPerformAnalysis = canPerformAnalysis(subscription);

  if (!subLoading && !userCanPerformAnalysis) {
    return (
      <div className="flex items-center justify-center py-16 px-4">
        <Card className="max-w-lg w-full text-center border-slate-200/60 bg-gradient-to-br from-indigo-50 to-indigo-100/50 rounded-2xl shadow-lg">
          <CardHeader className="p-8">
            <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg ring-2 ring-white/50">
              <Zap className="w-10 h-10 text-indigo-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900 mb-3 tracking-tight">
              Upgrade Your Plan
            </CardTitle>
            <CardDescription className="text-slate-600 mb-6 leading-relaxed text-base">
              You've reached the limit of your free analyses. Upgrade for unlimited analyses and advanced features.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-8 pt-0">
            <Button size="lg" className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 py-3 px-8" asChild>
              <Link to="/pricing">View Pricing Plans</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {!analysis ? (
        <>
          <Card className="bg-gradient-to-r from-slate-50 via-white to-white border-slate-200/60 rounded-2xl shadow-sm">
            <CardHeader className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-8">
              <div>
                <CardTitle className="text-2xl font-semibold text-slate-900 flex items-center gap-3 mb-2 tracking-tight">
                  <div className="h-8 w-8 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl flex items-center justify-center">
                    <UploadCloud className="w-4 h-4 text-white" />
                  </div>
                  {projectId ? 'Project Analysis' : 'New Analysis'}
                </CardTitle>
                <CardDescription className="text-slate-600">
                  {projectId 
                    ? 'Upload a candidate resume to analyze against this project\'s requirements.'
                    : 'Upload a Candidate CV and Job Description to get started.'
                  }
                </CardDescription>
              </div>
              <UsageIndicator />
            </CardHeader>
          </Card>

          {isLoading ? (
            <AnalysisLoading />
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card className="shadow-sm bg-gradient-to-br from-blue-50/50 to-blue-100/30 border-blue-200/40 rounded-2xl">
                  <CardContent className="p-0">
                    <CandidateForm
                      candidateName={values.candidateName}
                      cvText={values.cvText}
                      onNameChange={(value) => setFieldValue('candidateName', value)}
                      onCvTextChange={(value) => setFieldValue('cvText', value)}
                      errors={{
                        name: errors.candidateName,
                        cvText: errors.cvText
                      }}
                    />
                  </CardContent>
                </Card>

                <Card className="shadow-sm bg-gradient-to-br from-emerald-50/50 to-emerald-100/30 border-emerald-200/40 rounded-2xl">
                  <CardContent className="p-0">
                    <JobForm
                      jobTitle={values.jobTitle}
                      jobDescription={values.jobDescription}
                      onTitleChange={(value) => setFieldValue('jobTitle', value)}
                      onDescriptionChange={(value) => setFieldValue('jobDescription', value)}
                      errors={{
                        title: errors.jobTitle,
                        description: errors.jobDescription
                      }}
                      isProjectContext={!!projectId}
                    />
                  </CardContent>
                </Card>
              </div>

              <div className="sticky bottom-4 z-10 mt-8">
                <Card className="bg-white/95 backdrop-blur-md border border-slate-200/60 shadow-lg rounded-2xl">
                  <CardContent className="p-4 flex justify-end space-x-4">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={resetFormAndAnalysis}
                      disabled={isLoading}
                      size="sm"
                      className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-all duration-200"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Clear Form
                    </Button>
                    <Button
                      type="submit"
                      disabled={isLoading}
                      size="sm"
                      className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                    >
                      {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      {projectId ? 'Analyze for Project' : 'Analyze CV'}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </form>
          )}
        </>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <h2 className="text-2xl font-semibold text-slate-900 tracking-tight">Analysis Results</h2>
            <Button
              type="button"
              variant="default"
              onClick={resetFormAndAnalysis}
              size="sm"
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              {projectId ? 'Analyze Another Candidate' : 'Start New Analysis'}
            </Button>
          </div>
          <AnalysisResults 
            analysis={analysis} 
            projectId={projectId}
            onAnalysisComplete={onAnalysisComplete}
          />
        </div>
      )}
    </div>
  );
}